import { useState, useRef, useCallback, useEffect } from 'react';
import type { YoutubeSlideHandle } from '../components/YoutubeSlide';
import type { CaptureSlideHandle } from '../components/CaptureSlide';
import type { FaseOverlayAniversario } from '../components/OverlayAniversario';
import type {
  AudioCaptura,
  FonteReproducao,
  MomentoEspecialAudioFonte,
} from '@/shared/models/configuracoes.model';
import {
  calcularDuracaoCelebracaoSeg,
  calcularFimEfetivoAudioSeg,
} from '../../domain/usecases/calcularDuracaoCelebracao';
import { useAudioRecortado } from '@/shared/hooks/useAudioRecortado';

const AUDIO_PADRAO_SRC = '/parabens-music.mp3';

type SnapshotAudio = {
  fonte: MomentoEspecialAudioFonte;
  url: string | null;
  inicioSeg: number;
  fimEfetivoAudioSeg: number;
  duracaoCelebracaoSeg: number;
  audioSrc: string;
  youtubeVideoId: string | null;
};

type SnapshotMidia = {
  origemMidia: FonteReproducao;
  audioCaptura: AudioCaptura;
};

export interface UseMomentoEspecialParams {
  ytSlideRef:       React.RefObject<YoutubeSlideHandle | null>;
  ytLiveRef:        React.RefObject<YoutubeSlideHandle | null>;
  captureSlideRef:  React.RefObject<CaptureSlideHandle | null>;
  videoRef:         React.RefObject<HTMLVideoElement | null>;
  playerVolumeRef:  React.RefObject<number>;
  playerPlayingRef: React.RefObject<boolean>;
  playerMutedRef:   React.RefObject<boolean>;
  origemMidia:      FonteReproducao;
  audioCaptura:     AudioCaptura;
  sendToTela:       (event: string, payload: Record<string, unknown>) => void;
  ytUnlocked:       boolean;
  /** Config de `configuracoes_tela.momento_especial_audio_*` (via playerConfig). */
  audioFonte:       MomentoEspecialAudioFonte;
  audioUrl:         string | null;
  audioInicioSeg:   number;
  audioFimSeg:      number;
}

export interface UseMomentoEspecialReturn {
  momentoEspecial:      { nome: string } | null;
  celebracaoAtiva:      boolean;
  birthdayAudioRef:     React.RefObject<HTMLAudioElement | null>;
  trigger(nome: string, _videoUrl?: string | undefined): void;
  handleEnded(): void;
  onFaseChange(fase: FaseOverlayAniversario): void;
  /** Duração da fase de celebração, sincronizada com o recorte efetivo. */
  duracaoCelebracaoSeg: number;
  /** Início do recorte congelado durante o momento especial. */
  inicioSegEfetivo:     number;
  /** Fim do recorte limitado a no máximo 90 segundos após o início. */
  fimEfetivoAudioSeg:   number;
  /** `src` do elemento `<audio>` — só relevante quando a fonte não é YouTube. */
  audioSrc:             string;
  /** Video ID puro (já extraído no lado admin) quando a fonte é YouTube. */
  youtubeVideoId:       string | null;
}

/** Pausa as fontes do Player durante o Momento Especial e restaura exatamente
 * o snapshot de mídia/áudio capturado no disparo. */
export function useMomentoEspecial({
  ytSlideRef, ytLiveRef, captureSlideRef, videoRef,
  playerVolumeRef, playerPlayingRef, playerMutedRef,
  origemMidia, audioCaptura, sendToTela, ytUnlocked,
  audioFonte, audioUrl, audioInicioSeg, audioFimSeg,
}: UseMomentoEspecialParams): UseMomentoEspecialReturn {
  const [momentoEspecial, setMomentoEspecial] = useState<{ nome: string } | null>(null);
  const [celebracaoAtiva, setCelebracaoAtiva] = useState(false);
  const birthdayAudioRef = useRef<HTMLAudioElement>(null);
  const volumePreMomentoEspecialRef = useRef(100);
  const snapshotAudioRef = useRef<SnapshotAudio | null>(null);
  const snapshotMidiaRef = useRef<SnapshotMidia | null>(null);

  const snapshotAtivo = momentoEspecial ? snapshotAudioRef.current : null;
  const inicioSegEfetivo = snapshotAtivo?.inicioSeg ?? audioInicioSeg;
  const duracaoCelebracaoSeg = snapshotAtivo?.duracaoCelebracaoSeg
    ?? calcularDuracaoCelebracaoSeg(audioInicioSeg, audioFimSeg);
  const fimEfetivoAudioSeg = snapshotAtivo?.fimEfetivoAudioSeg
    ?? calcularFimEfetivoAudioSeg(audioInicioSeg, audioFimSeg);
  const audioSrc = snapshotAtivo?.audioSrc
    ?? (audioFonte === 'upload' && audioUrl ? audioUrl : AUDIO_PADRAO_SRC);
  const youtubeVideoId = snapshotAtivo
    ? snapshotAtivo.youtubeVideoId
    : (audioFonte === 'youtube' ? audioUrl : null);
  const fonteAudioAtiva = snapshotAtivo?.fonte ?? audioFonte;

  const onFaseChange = useCallback((fase: FaseOverlayAniversario) => {
    setCelebracaoAtiva(fase === 'celebracao');
  }, []);

  const trigger = useCallback((nome: string, _videoUrl?: string) => {
    const audioSrcSnapshot = audioFonte === 'upload' && audioUrl
      ? audioUrl
      : AUDIO_PADRAO_SRC;

    snapshotAudioRef.current = {
      fonte: audioFonte,
      url: audioUrl,
      inicioSeg: audioInicioSeg,
      fimEfetivoAudioSeg: calcularFimEfetivoAudioSeg(audioInicioSeg, audioFimSeg),
      duracaoCelebracaoSeg: calcularDuracaoCelebracaoSeg(audioInicioSeg, audioFimSeg),
      audioSrc: audioSrcSnapshot,
      youtubeVideoId: audioFonte === 'youtube' ? audioUrl : null,
    };
    snapshotMidiaRef.current = { origemMidia, audioCaptura };
    volumePreMomentoEspecialRef.current = playerVolumeRef.current;

    ytSlideRef.current?.setVolume(0);
    ytSlideRef.current?.mute();
    ytSlideRef.current?.pause();
    ytLiveRef.current?.setVolume(0);
    ytLiveRef.current?.mute();
    ytLiveRef.current?.pause();
    captureSlideRef.current?.setVolume(0);
    if (videoRef.current) {
      videoRef.current.volume = 0;
      videoRef.current.pause();
    }

    setCelebracaoAtiva(false);
    setMomentoEspecial({ nome });
  }, [
    audioFonte, audioUrl, audioInicioSeg, audioFimSeg, origemMidia, audioCaptura,
    playerVolumeRef, ytSlideRef, ytLiveRef, captureSlideRef, videoRef,
  ]);

  const handleEnded = useCallback(() => {
    const vol = volumePreMomentoEspecialRef.current;
    const playing = playerPlayingRef.current;
    const muted = playerMutedRef.current;
    const midia = snapshotMidiaRef.current ?? { origemMidia, audioCaptura };
    const slide = ytSlideRef.current;
    const live = ytLiveRef.current;

    setCelebracaoAtiva(false);
    setMomentoEspecial(null);
    playerVolumeRef.current = vol;
    if (videoRef.current) {
      videoRef.current.volume = vol / 100;
      if (playing) videoRef.current.play().catch(() => {});
    }
    if (midia.origemMidia === 'captura') {
      captureSlideRef.current?.setVolume(vol);
    }

    if (midia.origemMidia === 'playlist') {
      live?.mute();
      live?.pause();
      slide?.setVolume(vol);
      if (muted) slide?.mute();
      else slide?.unMute();
      if (playing) slide?.reloadAtCurrentTime();
    } else if (midia.origemMidia === 'transmissao') {
      live?.setVolume(vol);
      if (midia.audioCaptura === 'captura') {
        slide?.mute();
        slide?.pause();
        live?.reloadAtCurrentTime();
        if (muted) live?.mute();
        else live?.unMute();
      } else {
        live?.mute();
        slide?.setVolume(vol);
        if (muted) slide?.mute();
        else slide?.unMute();
        if (playing) {
          live?.reloadAtCurrentTime();
          slide?.reloadAtCurrentTime();
        }
      }
    } else {
      live?.mute();
      live?.pause();
      if (midia.audioCaptura === 'captura') {
        slide?.mute();
        slide?.pause();
      } else {
        slide?.setVolume(vol);
        if (muted) slide?.mute();
        else slide?.unMute();
        if (playing) slide?.reloadAtCurrentTime();
      }
    }

    sendToTela('estado_player', {
      isPlaying: playing,
      isMuted: muted,
      volume: vol,
      musicaAtual: null as unknown as Record<string, unknown>,
    });

    snapshotAudioRef.current = null;
    snapshotMidiaRef.current = null;
  }, [
    origemMidia, audioCaptura, ytSlideRef, ytLiveRef, captureSlideRef, videoRef,
    playerVolumeRef, playerPlayingRef, playerMutedRef, sendToTela,
  ]);

  // Primer: aproveita o gesto de desbloqueio do YouTube para registrar o áudio
  // de aniversário no contexto de áudio da página.
  useEffect(() => {
    if (!ytUnlocked) return;
    const audio = birthdayAudioRef.current;
    if (!audio) return;
    audio.play().then(() => audio.pause()).catch(() => {});
  }, [ytUnlocked]);

  useAudioRecortado({
    audioRef: birthdayAudioRef,
    ativo: celebracaoAtiva && fonteAudioAtiva !== 'youtube',
    inicioSeg: inicioSegEfetivo,
    fimSeg: fimEfetivoAudioSeg,
    src: fonteAudioAtiva === 'youtube' ? null : audioSrc,
  });

  return {
    momentoEspecial,
    celebracaoAtiva,
    birthdayAudioRef,
    trigger,
    handleEnded,
    onFaseChange,
    duracaoCelebracaoSeg,
    inicioSegEfetivo,
    fimEfetivoAudioSeg,
    audioSrc,
    youtubeVideoId,
  };
}
